import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Download, RefreshCw, Slash, X } from "lucide-react";
import {
  bulkIgnore,
  bulkRetry,
  countDomains,
  exportDomainsUrl,
  type BulkResult,
  type DomainFilters,
} from "@/api/domains";

// SPA prompts the user before exports over this size since a no-filter
// dump can run to 6 M+ rows; below the threshold the buttons are
// one-click. Stays in sync with the bulk-action 50K display threshold —
// arbitrary but consistent with the "filter matches N+" labelling.
const _EXPORT_CONFIRM_THRESHOLD = 50_000;

export function BulkActionBar({
  selectedIds,
  filter,
  onClearSelection,
}: {
  selectedIds: string[];
  filter: DomainFilters;
  onClearSelection: () => void;
}) {
  const qc = useQueryClient();
  const [confirm, setConfirm] = useState<null | "retry" | "ignore">(null);
  const [exportConfirm, setExportConfirm] = useState<null | "csv" | "jsonl">(null);

  const doExport = (format: "csv" | "jsonl") => {
    // Browser-triggered download. The API's `Content-Disposition` header
    // tells the browser to save (not navigate), so the user stays on the
    // current page. Session cookie carries auth.
    window.location.assign(exportDomainsUrl(filter, format));
  };

  const startExport = (format: "csv" | "jsonl") => {
    const count = filterCount.data?.count ?? 0;
    const capped = filterCount.data?.capped ?? false;
    if (capped || count > _EXPORT_CONFIRM_THRESHOLD) {
      setExportConfirm(format);
    } else {
      doExport(format);
    }
  };

  const filterIsNonTrivial =
    !!(filter.status || filter.tag || filter.run_id || filter.q);

  const filterCount = useQuery({
    queryKey: ["domains", "count", filter],
    queryFn: () => countDomains(filter),
    enabled: filterIsNonTrivial,
    staleTime: 10_000,
  });

  const onSuccess = (result: BulkResult, label: string) => {
    if (result.async) {
      toast.success(`${label} queued · ${result.affected.toLocaleString()} domains will run via worker`);
    } else {
      toast.success(`${label} applied to ${result.affected.toLocaleString()} domain(s)`);
    }
    if (result.capped) toast.warning("Match was capped — re-run to continue.");
    qc.invalidateQueries({ queryKey: ["domains"] });
    qc.invalidateQueries({ queryKey: ["domains", "count"] });
    onClearSelection();
    setConfirm(null);
  };

  const retry = useMutation({
    mutationFn: (mode: "ids" | "filter") =>
      bulkRetry(mode === "ids" ? { ids: selectedIds } : { filter }),
    onSuccess: (r, mode) => onSuccess(r, mode === "ids" ? "Retry" : "Bulk retry"),
  });

  const ignore = useMutation({
    mutationFn: (mode: "ids" | "filter") =>
      bulkIgnore(mode === "ids" ? { ids: selectedIds } : { filter }),
    onSuccess: (r, mode) => onSuccess(r, mode === "ids" ? "Ignore" : "Bulk ignore"),
  });

  const visible = selectedIds.length > 0 || filterIsNonTrivial;
  if (!visible) return null;

  const filterMatchLabel =
    filterCount.data === undefined
      ? "…"
      : filterCount.data.capped
        ? `${filterCount.data.count.toLocaleString()}+`
        : filterCount.data.count.toLocaleString();

  return (
    <>
      <div className="sticky bottom-0 z-30 mt-4 -mx-6 px-6 py-3 border-t border-border bg-card shadow-[0_-4px_12px_rgba(0,0,0,0.1)] flex flex-wrap items-center gap-3">
        {selectedIds.length > 0 ? (
          <>
            <span className="text-sm">
              <strong className="tabular-nums">{selectedIds.length.toLocaleString()}</strong> selected
            </span>
            <button
              className="btn"
              onClick={() => retry.mutate("ids")}
              disabled={retry.isPending}
            >
              <RefreshCw className="h-4 w-4" /> Retry selected
            </button>
            <button
              className="btn"
              onClick={() => ignore.mutate("ids")}
              disabled={ignore.isPending}
            >
              <Slash className="h-4 w-4" /> Ignore selected
            </button>
            <button className="btn-ghost text-xs" onClick={onClearSelection}>
              <X className="h-3.5 w-3.5" /> Clear selection
            </button>
            <span className="text-muted text-xs">·</span>
          </>
        ) : null}

        {filterIsNonTrivial ? (
          <>
            <span className="text-sm text-muted">
              Filter matches <strong className="text-fg tabular-nums">{filterMatchLabel}</strong>
            </span>
            <button
              className="btn"
              onClick={() => setConfirm("retry")}
              disabled={retry.isPending}
            >
              Retry all matching
            </button>
            <button
              className="btn"
              onClick={() => setConfirm("ignore")}
              disabled={ignore.isPending}
            >
              Ignore all matching
            </button>
            <button
              className="btn"
              onClick={() => startExport("csv")}
              title="Download matching domains as CSV"
            >
              <Download className="h-4 w-4" /> Export CSV
            </button>
            <button
              className="btn"
              onClick={() => startExport("jsonl")}
              title="Download matching domains as JSONL (one JSON object per line)"
            >
              <Download className="h-4 w-4" /> Export JSONL
            </button>
          </>
        ) : null}
      </div>

      {confirm ? (
        <ConfirmModal
          title={confirm === "retry" ? "Retry all matching?" : "Ignore all matching?"}
          message={`This applies to ${filterMatchLabel} domain(s). The action runs in the background once the match exceeds 10,000 rows.`}
          danger={confirm === "ignore"}
          onCancel={() => setConfirm(null)}
          onConfirm={() => (confirm === "retry" ? retry : ignore).mutate("filter")}
          busy={retry.isPending || ignore.isPending}
        />
      ) : null}

      {exportConfirm ? (
        <ConfirmModal
          title={`Export ${exportConfirm.toUpperCase()}?`}
          message={
            `About to export ${filterMatchLabel} matching domain(s). `
            + "The download streams progressively — leave the tab open."
          }
          onCancel={() => setExportConfirm(null)}
          onConfirm={() => {
            doExport(exportConfirm);
            setExportConfirm(null);
            toast.info("Export starting — check your downloads.");
          }}
        />
      ) : null}
    </>
  );
}

function ConfirmModal({
  title,
  message,
  danger,
  onCancel,
  onConfirm,
  busy,
}: {
  title: string;
  message: string;
  danger?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  busy?: boolean;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="card w-full max-w-md p-5 space-y-3">
        <h3 className="text-base font-semibold">{title}</h3>
        <p className="text-sm text-muted">{message}</p>
        <div className="flex justify-end gap-2">
          <button className="btn-ghost" onClick={onCancel} disabled={busy}>Cancel</button>
          <button
            className={danger ? "btn-danger" : "btn-primary"}
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? "Working…" : "Confirm"}
          </button>
        </div>
      </div>
    </div>
  );
}
